import { __decorate } from "tslib";
import React, { Children, isValidElement } from 'react';
import { action, computed, get, observable, runInAction, set } from 'mobx';
import sortBy from 'lodash/sortBy';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';
import isNumber from 'lodash/isNumber';
import measureScrollbar from 'choerodon-ui/lib/_util/measureScrollbar';
import { isCalcSize, toPx } from 'choerodon-ui/lib/_util/UnitConvertor';
import { getConfig, getCustomizable, getProPrefixCls } from 'choerodon-ui/lib/configure';
import { getTooltip } from 'choerodon-ui/lib/_util/TooltipUtils';
import Icon from 'choerodon-ui/lib/icon';
import isFunction from 'lodash/isFunction';
import { ColumnDefaultProps } from './Column';
import CustomizationSettings from './customization-settings/CustomizationSettings';
import isFragment from '../_util/isFragment';
import ObserverCheckBox from '../check-box/CheckBox';
import ObserverRadio from '../radio/Radio';
import { DataSetSelection } from '../data-set/enum';
import { ColumnAlign, ColumnLock, DragColumnAlign, RowBoxPlacement, ScrollPosition, SelectionMode, TableAutoHeightType, TableColumnTooltip, TableEditMode, TableHeightType, TableMode, } from './enum';
import { stopPropagation } from '../_util/EventManager';
import { getColumnKey, getHeader } from './utils';
import getReactNodeText from '../_util/getReactNodeText';
import ColumnGroups from './ColumnGroups';
import autobind from '../_util/autobind';
import { Size } from '../core/enum';
import { $l } from '../locale-context';
import CustomizationColumnHeader from './customization-settings/CustomizationColumnHeader';
import Dropdown from '../dropdown/Dropdown';
import Menu from '../menu';
import { treeSome } from '../_util/treeUtils';
export const SELECTION_KEY = '__selection-column__'; // TODO:Symbol
export const ROW_NUMBER_KEY = '__row-number-column__'; // TODO:Symbol
export const DRAG_KEY = '__drag-column__'; // TODO:Symbol
export const EXPAND_KEY = '__expand-column__'; // TODO:Symbol
export const CUSTOMIZED_KEY = '__customized-column__'; // TODO:Symbol
export const AGGREGATION_EXPAND_CELL_KEY = '__aggregation-expand-cell__'; // TODO:Symbol
function columnFilter(column) {
    return Boolean(column);
}
// function hasProperty<T>(target: T, property: string): (keyof T) is undefined {
//   return property in target;
// }
export function getIdList(store) {
    const { mouseBatchChooseStartId, mouseBatchChooseEndId, node: { element }, prefixCls } = store;
    const rows = Array.from(element.querySelectorAll(`.${prefixCls}-row`));
    let endId;
    const idList = [];
    rows.some((row) => {
        const index = Number(row.dataset.index);
        if (!endId) {
            if (mouseBatchChooseStartId === index) {
                endId = mouseBatchChooseEndId;
            }
            else if (mouseBatchChooseEndId === index) {
                endId = mouseBatchChooseStartId;
            }
        }
        if (endId) {
            idList.push(index);
        }
        return endId === index;
    });
    return idList;
}
function getRowNumbers(record, dataSet, isTree) {
    if (record && dataSet) {
        const { paging, currentPage, pageSize } = dataSet;
        const pageIndex = (isTree ? paging === 'server' : paging) ? (currentPage - 1) * pageSize : 0;
        if (isTree) {
            return record.path.map((r, index) => r.indexInParent + 1 + (index === 0 ? pageIndex : 0));
        }
        return [record.index + 1 + pageIndex];
    }
    return [0];
}
function hasCheckField({ editor, name, hidden }, checkField) {
    return !hidden && !!editor && checkField === name;
}
function renderSelectionBox({ record, store }) {
    const { dataSet } = record;
    if (dataSet) {
        const { selection } = dataSet;
        const handleChange = value => {
            if (store.props.selectionMode === SelectionMode.mousedown) {
                // 将处理逻辑交给 mousedown 的处理逻辑 不然会两次触发导致不被勾选上
                return;
            }
            if (value) {
                dataSet.select(record);
            }
            else {
                dataSet.unSelect(record);
            }
        };
        const handleClick = e => {
            stopPropagation(e);
            if (selection === DataSetSelection.multiple) {
                const { lastSelected } = store;
                if (lastSelected) {
                    const nativeEvent = e.nativeEvent;
                    let startIndex = -1;
                    let endIndex = -1;
                    if (nativeEvent.shiftKey) {
                        const pointKeys = new Set([lastSelected.key, record.key]);
                        dataSet.some((pointRecord, index) => {
                            if (pointKeys.has(pointRecord.key)) {
                                if (startIndex === -1) {
                                    startIndex = index;
                                }
                                else {
                                    endIndex = index;
                                    return true;
                                }
                            }
                            return false;
                        });
                    }
                    if (endIndex !== -1 && startIndex !== endIndex) {
                        // Batch update selections
                        const rangeRecords = dataSet.slice(startIndex, endIndex + 1);
                        const changedRecords = [];
                        const selectedKeys = new Set(dataSet.selected.map(selected => selected.key));
                        if (record.isSelected) {
                            rangeRecords.forEach(rangeRecord => {
                                if (selectedKeys.has(rangeRecord.key)) {
                                    changedRecords.push(rangeRecord);
                                }
                            });
                            dataSet.batchUnSelect(changedRecords);
                        }
                        else {
                            rangeRecords.forEach(rangeRecord => {
                                if (!selectedKeys.has(rangeRecord.key)) {
                                    changedRecords.push(rangeRecord);
                                }
                            });
                            dataSet.batchSelect(changedRecords);
                        }
                    }
                }
                store.lastSelected = record;
            }
            else if (record.isSelected) {
                dataSet.unSelect(record);
            }
        };
        if (selection === DataSetSelection.multiple) {
            const batchSelectProps = {};
            const handleDragMouseUp = action(() => {
                const { mouseBatchChooseIdList } = store;
                if (store.mouseBatchChooseState) {
                    store.mouseBatchChooseState = false;
                    store.changeMouseBatchChooseIdList([]);
                    const { mouseBatchChooseStartId, mouseBatchChooseEndId } = store;
                    if (mouseBatchChooseStartId === mouseBatchChooseEndId) {
                        return;
                    }
                    const startRecord = dataSet.findRecordById(mouseBatchChooseStartId);
                    const { isSelected } = startRecord || {};
                    if (isSelected) {
                        dataSet.batchUnSelect(mouseBatchChooseIdList);
                    }
                    else {
                        dataSet.batchSelect(mouseBatchChooseIdList);
                    }
                }
                document.removeEventListener('pointerup', handleDragMouseUp);
            });
            if (store.useMouseBatchChoose) {
                batchSelectProps.onMouseDown = action(() => {
                    store.mouseBatchChooseStartId = record.id;
                    store.mouseBatchChooseEndId = record.id;
                    store.mouseBatchChooseState = true;
                    // 为什么使用 pointerup
                    // 因为需要对disabled的元素进行特殊处理
                    // 因为状态的改变依赖 mouseup 而在disabled的元素上 无法触发mouseup事件
                    // 导致状态无法进行修正
                    // 以下两种方案通过 pointer-events:none 进行处理
                    // https://stackoverflow.com/questions/322378/javascript-check-if-mouse-button-down
                    // https://stackoverflow.com/questions/62081666/the-event-of-the-document-is-not-triggered-when-it-is-on-a-disabled-element
                    // 而使用指针事件可以突破disabled的限制
                    // https://stackoverflow.com/questions/62126515/how-to-get-the-state-of-the-mouse-through-javascript/62127845#62127845
                    document.addEventListener('pointerup', handleDragMouseUp);
                });
                batchSelectProps.onMouseEnter = () => {
                    if (store.mouseBatchChooseState) {
                        store.mouseBatchChooseEndId = record.id;
                        store.changeMouseBatchChooseIdList(getIdList(store));
                    }
                };
            }
            return (React.createElement(ObserverCheckBox, Object.assign({}, batchSelectProps, { checked: record.isSelected, onChange: handleChange, onClick: handleClick, disabled: !record.selectable, "data-selection-key": SELECTION_KEY, value: true })));
        }
        if (selection === DataSetSelection.single) {
            return (React.createElement(ObserverRadio, { checked: record.isSelected, onChange: handleChange, onClick: handleClick, disabled: !record.selectable, "data-selection-key": SELECTION_KEY, value: true }));
        }
    }
}
export function mergeDefaultProps(originalColumns, tableAggregation, customizedColumns, parent = null, defaultKey = [0], columnSort = {
    left: 0,
    center: 0,
    right: 0,
}) {
    const columns = [];
    const leftColumns = [];
    const rightColumns = [];
    let hasAggregationColumn = false;
    originalColumns.forEach((column) => {
        if (isPlainObject(column)) {
            const newColumn = { ...ColumnDefaultProps, ...column };
            if (isNil(getColumnKey(newColumn))) {
                newColumn.key = `anonymous-${defaultKey[0]++}`;
            }
            const { children, aggregation } = newColumn;
            if (!hasAggregationColumn && aggregation) {
                hasAggregationColumn = true;
            }
            if (tableAggregation || !aggregation) {
                if (customizedColumns) {
                    Object.assign(newColumn, customizedColumns[getColumnKey(newColumn).toString()]);
                }
                if (parent) {
                    newColumn.lock = parent.lock;
                }
                if (children) {
                    const [, childrenColumns, , childrenHasAffregationColumn] = mergeDefaultProps(children, tableAggregation, customizedColumns, newColumn, defaultKey);
                    newColumn.children = childrenColumns;
                    if (!hasAggregationColumn && childrenHasAffregationColumn) {
                        hasAggregationColumn = childrenHasAffregationColumn;
                    }
                }
                if (parent || !newColumn.lock) {
                    if (newColumn.sort === undefined) {
                        newColumn.sort = columnSort.center;
                    }
                    columnSort.center++;
                    columns.push(newColumn);
                }
                else if (newColumn.lock === true || newColumn.lock === ColumnLock.left) {
                    if (newColumn.sort === undefined) {
                        newColumn.sort = columnSort.left;
                    }
                    columnSort.left++;
                    leftColumns.push(newColumn);
                }
                else {
                    if (newColumn.sort === undefined) {
                        newColumn.sort = columnSort.right;
                    }
                    columnSort.right++;
                    rightColumns.push(newColumn);
                }
            }
            else if (children) {
                const [, nodes, , childrenHasAffregationColumn] = mergeDefaultProps(children, tableAggregation, customizedColumns, parent, defaultKey, parent ? undefined : columnSort);
                if (!hasAggregationColumn && childrenHasAffregationColumn) {
                    hasAggregationColumn = childrenHasAffregationColumn;
                }
                if (parent) {
                    parent.children = nodes;
                }
                else {
                    nodes.forEach((node) => {
                        if (!node.lock) {
                            columns.push(node);
                        }
                        else if (node.lock === true || node.lock === ColumnLock.left) {
                            leftColumns.push(node);
                        }
                        else {
                            rightColumns.push(node);
                        }
                    });
                }
            }
        }
    }, []);
    if (parent) {
        return [[], sortBy(columns, ({ sort }) => sort), [], hasAggregationColumn];
    }
    return [
        sortBy(leftColumns, ({ sort }) => sort),
        sortBy(columns, ({ sort }) => sort),
        sortBy(rightColumns, ({ sort }) => sort),
        hasAggregationColumn,
    ];
}
export function normalizeColumns(elements, tableAggregation, customizedColumns, parent = null, defaultKey = [0], columnSort = {
    left: 0,
    center: 0,
    right: 0,
}) {
    const columns = [];
    const leftColumns = [];
    const rightColumns = [];
    let hasAggregationColumn = false;
    const normalizeColumn = (element) => {
        if (isValidElement(element)) {
            const { props, key, type } = element;
            if (isFragment(element)) {
                const { children } = props;
                if (children) {
                    Children.forEach(children, normalizeColumn);
                }
            }
            else if (type.__PRO_TABLE_COLUMN) {
                const column = {
                    ...props,
                };
                if (key) {
                    column.key = key;
                }
                else if (isNil(getColumnKey(column))) {
                    column.key = `anonymous-${defaultKey[0]++}`;
                }
                const { children, aggregation } = column;
                if (!hasAggregationColumn && aggregation) {
                    hasAggregationColumn = true;
                }
                if (tableAggregation || !aggregation) {
                    if (customizedColumns) {
                        Object.assign(column, customizedColumns[getColumnKey(column).toString()]);
                    }
                    if (parent) {
                        column.lock = parent.lock;
                    }
                    const [, childrenColumns, , childrenHasAffregationColumn] = normalizeColumns(children, tableAggregation, customizedColumns, column, defaultKey);
                    column.children = childrenColumns;
                    if (!hasAggregationColumn && childrenHasAffregationColumn) {
                        hasAggregationColumn = childrenHasAffregationColumn;
                    }
                    if (parent || !column.lock) {
                        if (column.sort === undefined) {
                            column.sort = columnSort.center;
                        }
                        columnSort.center++;
                        columns.push(column);
                    }
                    else if (column.lock === true || column.lock === ColumnLock.left) {
                        if (column.sort === undefined) {
                            column.sort = columnSort.left;
                        }
                        columnSort.left++;
                        leftColumns.push(column);
                    }
                    else {
                        if (column.sort === undefined) {
                            column.sort = columnSort.right;
                        }
                        columnSort.right++;
                        rightColumns.push(column);
                    }
                }
                else {
                    const [, nodes, , childrenHasAffregationColumn] = normalizeColumns(children, tableAggregation, customizedColumns, parent, defaultKey, parent ? undefined : columnSort);
                    if (!hasAggregationColumn && childrenHasAffregationColumn) {
                        hasAggregationColumn = childrenHasAffregationColumn;
                    }
                    if (parent) {
                        parent.children = nodes;
                    }
                    else {
                        nodes.forEach((node) => {
                            if (!node.lock) {
                                columns.push(node);
                            }
                            else if (node.lock === true || node.lock === ColumnLock.left) {
                                leftColumns.push(node);
                            }
                            else {
                                rightColumns.push(node);
                            }
                        });
                    }
                }
            }
        }
    };
    Children.forEach(elements, normalizeColumn);
    if (parent) {
        return [[], sortBy(columns, ({ sort }) => sort), [], hasAggregationColumn];
    }
    return [
        sortBy(leftColumns, ({ sort }) => sort),
        sortBy(columns, ({ sort }) => sort),
        sortBy(rightColumns, ({ sort }) => sort),
        hasAggregationColumn,
    ];
}
async function getHeaderTexts(dataSet, columns, aggregation, headers = []) {
    const column = columns.shift();
    if (column) {
        headers.push({ name: column.name, label: await getReactNodeText(getHeader(column, dataSet, aggregation)) });
    }
    if (columns.length) {
        await getHeaderTexts(dataSet, columns, aggregation, headers);
    }
    return headers;
}
export default class TableStore {
    constructor(node) {
        this.editors = new Map();
        this.mouseBatchChooseStartId = 0;
        this.mouseBatchChooseEndId = 0;
        this.inBatchExpansion = false;
        this.performanceOn = false;
        this.timing = { renderStart: 0, renderEnd: 0 };
        this.handleSelectAllChange = action(value => {
            const { dataSet, filter } = this.props;
            const isSelectAll = value ? dataSet.currentSelected.length === dataSet.records.filter(record => record.selectable).length : !value;
            if (!isSelectAll) {
                dataSet.selectAll(filter);
            }
            else {
                dataSet.unSelectAll();
                if (this.showCachedSelection) {
                    dataSet.clearCachedSelected();
                }
            }
        });
        this.saveCustomizedDebounce = debounce(this.saveCustomized, 1000);
        runInAction(() => {
            this.scrollPosition = ScrollPosition.left;
            this.mouseBatchChooseIdList = [];
            this.mouseBatchChooseState = false;
            this.showCachedSelection = false;
            this.lockColumnsHeadRowsHeight = {};
            this.lockColumnsBodyRowsHeight = {};
            this.lockColumnsFootRowsHeight = {};
            this.node = node;
            this.expandedRows = [];
            this.lastScrollTop = 0;
            this.customizedActiveKey = ['columns'];
            this.leftOriginalColumns = [];
            this.originalColumns = [];
            this.rightOriginalColumns = [];
            this.tempCustomized = { columns: {} };
            this.customized = { columns: {} };
            this.setProps(node.props);
            if (this.customizable) {
                this.loadCustomized().then(this.handleLoadCustomized);
            }
            else {
                this.initColumns();
            }
        });
    }
    get bodyHeight() {
        return this.virtual ? this.virtualHeight : this.calcBodyHeight;
    }
    get stickyLeft() {
        return [ScrollPosition.right, ScrollPosition.middle].includes(this.scrollPosition);
    }
    get stickyRight() {
        return [ScrollPosition.left, ScrollPosition.middle].includes(this.scrollPosition);
    }
    get performanceEnabled() {
        const performanceEnabled = getConfig('performanceEnabled');
        return performanceEnabled && performanceEnabled.Table;
    }
    get dataSet() {
        return this.props.dataSet;
    }
    get prefixCls() {
        const { suffixCls, prefixCls } = this.props;
        return getProPrefixCls(suffixCls, prefixCls);
    }
    get customizable() {
        const { customizedCode } = this.props;
        if (customizedCode && (this.columnTitleEditable || this.columnDraggable || this.columnHideable)) {
            if ('customizable' in this.props) {
                return this.props.customizable;
            }
            return getConfig('tableCustomizable') || getCustomizable('Table');
        }
        return false;
    }
    get aggregation() {
        const { aggregation } = this.customized;
        if (aggregation !== undefined) {
            return aggregation;
        }
        const { aggregation: propAggregation } = this.props;
        return propAggregation;
    }
    get aggregationExpandType() {
        return this.customized.aggregationExpandType || 'cell';
    }
    get autoHeight() {
        const { autoHeight } = this.props;
        if (autoHeight) {
            const defaultAutoHeight = {
                type: TableAutoHeightType.minHeight,
                diff: getConfig('tableAutoHeightDiff') || 80,
            };
            if (isObject(autoHeight)) {
                return {
                    ...defaultAutoHeight,
                    ...autoHeight,
                };
            }
            return defaultAutoHeight;
        }
        return undefined;
    }
    get heightType() {
        const tempHeightType = get(this.tempCustomized, 'heightType');
        if (tempHeightType !== undefined) {
            return tempHeightType;
        }
        const { heightType } = this.customized;
        if (heightType !== undefined) {
            return heightType;
        }
        return this.originalHeightType;
    }
    get originalHeightType() {
        const { style, autoHeight } = this.props;
        if (autoHeight) {
            return TableHeightType.flex;
        }
        if (style) {
            const { height } = style;
            if (isString(height) && isCalcSize(height)) {
                return TableHeightType.flex;
            }
            if (isNumber(toPx(height))) {
                return TableHeightType.fixed;
            }
        }
        return TableHeightType.auto;
    }
    get virtualCell() {
        if ('virtualCell' in this.props) {
            return this.props.virtualCell;
        }
        return getConfig('tableVirtualCell');
    }
    /**
     * number 矫正虚拟滚动由于样式问题导致的高度不符问题
     */
    get virtualRowHeight() {
        const { virtualRowHeight } = this.props;
        if (virtualRowHeight) {
            return virtualRowHeight;
        }
        return isNumber(this.rowHeight) ? this.rowHeight + 3 : 33;
    }
    get virtual() {
        if (this.height !== undefined && isNumber(this.virtualRowHeight)) {
            if ('virtual' in this.props) {
                return this.props.virtual;
            }
            return getConfig('tableVirtual');
        }
        return false;
    }
    get virtualHeight() {
        const { virtualRowHeight, data } = this;
        return Math.round(data.length * virtualRowHeight);
    }
    get virtualStartIndex() {
        const { virtualRowHeight, lastScrollTop } = this;
        return Math.max(Math.round((lastScrollTop / virtualRowHeight) - 3), 0);
    }
    get virtualEndIndex() {
        const { virtualRowHeight, lastScrollTop, height, data } = this;
        return Math.min(height !== undefined ? Math.round((lastScrollTop + height) / virtualRowHeight) + 3 : Infinity, data.length);
    }
    get virtualTop() {
        const { virtualRowHeight, virtualStartIndex } = this;
        return virtualStartIndex * virtualRowHeight;
    }
    get virtualData() {
        const { data, virtual } = this;
        if (virtual) {
            const { virtualEndIndex, virtualStartIndex } = this;
            return data.slice(virtualStartIndex, virtualEndIndex);
        }
        return data;
    }
    get hidden() {
        return this.styledHidden || this.props.hidden;
    }
    get alwaysShowRowBox() {
        if ('alwaysShowRowBox' in this.props) {
            return this.props.alwaysShowRowBox;
        }
        const alwaysShowRowBox = getConfig('tableAlwaysShowRowBox');
        if (typeof alwaysShowRowBox !== 'undefined') {
            return alwaysShowRowBox;
        }
        return false;
    }
    get keyboard() {
        if ('keyboard' in this.props) {
            return this.props.keyboard;
        }
        const keyboard = getConfig('tableKeyboard');
        if (typeof keyboard !== 'undefined') {
            return keyboard;
        }
        return false;
    }
    get columnResizable() {
        if (this.currentEditRecord) {
            return false;
        }
        if ('columnResizable' in this.props) {
            return this.props.columnResizable;
        }
        return getConfig('tableColumnResizable') !== false;
    }
    get columnHideable() {
        if ('columnHideable' in this.props) {
            return this.props.columnHideable;
        }
        return getConfig('tableColumnHideable') !== false;
    }
    /**
     * 表头支持编辑
     */
    get columnTitleEditable() {
        if ('columnTitleEditable' in this.props) {
            return this.props.columnTitleEditable;
        }
        return getConfig('tableColumnTitleEditable') === true;
    }
    get pagination() {
        if ('pagination' in this.props) {
            return this.props.pagination;
        }
        return getConfig('pagination');
    }
    get dragColumnAlign() {
        if ('dragColumnAlign' in this.props) {
            return this.props.dragColumnAlign;
        }
        return getConfig('tableDragColumnAlign');
    }
    get columnDraggable() {
        if ('columnDraggable' in this.props) {
            return this.props.columnDraggable;
        }
        if ('dragColumn' in this.props) {
            return this.props.dragColumn;
        }
        if (getConfig('tableColumnDraggable') === true) {
            return true;
        }
        return getConfig('tableDragColumn') === true;
    }
    get rowDraggable() {
        if (this.isTree) {
            return false;
        }
        if ('rowDraggable' in this.props) {
            return this.props.rowDraggable;
        }
        if ('dragRow' in this.props) {
            return this.props.dragRow;
        }
        if (getConfig('tableRowDraggable') === true) {
            return true;
        }
        return getConfig('tableDragRow') === true;
    }
    get size() {
        const { size } = this.customized;
        if (size !== undefined) {
            return size;
        }
        return this.props.size || Size.default;
    }
    get rowHeight() {
        let rowHeight = 30;
        if ('rowHeight' in this.props) {
            rowHeight = this.props.rowHeight;
        }
        else {
            const tableRowHeight = getConfig('tableRowHeight');
            if (typeof tableRowHeight !== 'undefined') {
                rowHeight = tableRowHeight;
            }
        }
        if (isNumber(rowHeight)) {
            switch (this.size) {
                case Size.large:
                    return rowHeight + 2;
                case Size.small:
                    return rowHeight - 2;
                default:
            }
        }
        return rowHeight;
    }
    get autoFootHeight() {
        if ('autoFootHeight' in this.props) {
            return this.props.autoFootHeight;
        }
        return false;
    }
    get emptyText() {
        return getConfig('renderEmpty')('Table');
    }
    get highLightRow() {
        if ('highLightRow' in this.props) {
            return this.props.highLightRow;
        }
        return getConfig('tableHighLightRow');
    }
    get parityRow() {
        const { parityRow } = this.customized;
        if (parityRow !== undefined) {
            return parityRow;
        }
        if ('parityRow' in this.props) {
            return this.props.parityRow;
        }
        return getConfig('tableParityRow') === true;
    }
    get showRemovedRow() {
        if ('showRemovedRow' in this.props) {
            return this.props.showRemovedRow;
        }
        return getConfig('tableShowRemovedRow') === true;
    }
    get autoFocus() {
        if ('autoFocus' in this.props) {
            return this.props.autoFocus;
        }
        return getConfig('tableAutoFocus') !== false;
    }
    get selectedHighLightRow() {
        if ('selectedHighLightRow' in this.props) {
            return this.props.selectedHighLightRow;
        }
        return getConfig('tableSelectedHighLightRow') !== false;
    }
    get editorNextKeyEnterDown() {
        if ('editorNextKeyEnterDown' in this.props) {
            return this.props.editorNextKeyEnterDown;
        }
        return getConfig('tableEditorNextKeyEnterDown') !== false;
    }
    get border() {
        if ('border' in this.props) {
            return this.props.border;
        }
        return getConfig('tableBorder') !== false;
    }
    get columnEditorBorder() {
        if ('columnEditorBorder' in this.props) {
            return this.props.columnEditorBorder;
        }
        const tableColumnEditorBorder = getConfig('tableColumnEditorBorder');
        if (tableColumnEditorBorder !== undefined) {
            return tableColumnEditorBorder;
        }
        return this.border;
    }
    get queryBar() {
        return this.props.queryBar || getConfig('queryBar');
    }
    get expandIcon() {
        return this.props.expandIcon || getConfig('tableExpandIcon');
    }
    // get pristine(): boolean | undefined {
    //   return this.props.pristine;
    // }
    get currentEditRecord() {
        return this.dataSet.find(record => record.editing === true);
    }
    set currentEditRecord(record) {
        runInAction(() => {
            const { currentEditRecord, dataSet } = this;
            if (currentEditRecord) {
                if (currentEditRecord.isNew) {
                    dataSet.remove(currentEditRecord);
                }
                else {
                    currentEditRecord.reset();
                    currentEditRecord.editing = false;
                }
            }
            if (record) {
                record.editing = true;
            }
        });
    }
    get isTree() {
        return this.props.mode === TableMode.tree;
    }
    get editing() {
        return this.currentEditorName !== undefined || this.currentEditRecord !== undefined;
    }
    get hasRowBox() {
        const { dataSet, selectionMode } = this.props;
        const { alwaysShowRowBox } = this;
        if (dataSet) {
            const { selection } = dataSet;
            return selection && (selectionMode === SelectionMode.rowbox || alwaysShowRowBox);
        }
        return false;
    }
    get useMouseBatchChoose() {
        const { useMouseBatchChoose } = this.props;
        if (useMouseBatchChoose !== undefined) {
            return useMouseBatchChoose;
        }
        return getConfig('tableUseMouseBatchChoose');
    }
    get showSelectionTips() {
        const { showSelectionTips } = this.props;
        if (showSelectionTips !== undefined) {
            return showSelectionTips;
        }
        const tableShowSelectionTips = getConfig('tableShowSelectionTips');
        if (tableShowSelectionTips !== undefined) {
            return tableShowSelectionTips;
        }
        return false;
    }
    get overflowX() {
        const { width } = this;
        if (width !== undefined) {
            return this.columnGroups.width > width;
        }
        return false;
    }
    get overflowY() {
        const { bodyHeight, height } = this;
        return (bodyHeight !== undefined &&
            height !== undefined &&
            height < bodyHeight + (this.overflowX && !this.hasFooter ? measureScrollbar() : 0));
    }
    customizedColumnHeader() {
        return React.createElement(CustomizationColumnHeader, { onHeaderClick: this.openCustomizationModal });
    }
    get customizedColumn() {
        if (this.customizable && (!this.rowDraggable || this.dragColumnAlign !== DragColumnAlign.right)) {
            return {
                key: CUSTOMIZED_KEY,
                resizable: false,
                titleEditable: false,
                align: ColumnAlign.center,
                width: 30,
                lock: ColumnLock.right,
                header: this.customizedColumnHeader,
                headerClassName: `${this.prefixCls}-customized-column`,
            };
        }
        return undefined;
    }
    get expandColumn() {
        if (this.expandIconAsCell) {
            return {
                key: EXPAND_KEY,
                resizable: false,
                titleEditable: false,
                align: ColumnAlign.center,
                width: 50,
                lock: true,
            };
        }
        return undefined;
    }
    get rowNumberColumn() {
        const { rowNumber } = this.props;
        if (rowNumber) {
            return {
                key: ROW_NUMBER_KEY,
                resizable: false,
                titleEditable: false,
                headerClassName: `${this.prefixCls}-row-number-column`,
                renderer: this.renderRowNumber,
                tooltip: TableColumnTooltip.overflow,
                align: ColumnAlign.center,
                width: 50,
                lock: true,
            };
        }
        return undefined;
    }
    get selectionColumn() {
        if (this.hasRowBox) {
            const { dataSet, prefixCls } = this;
            const { rowBoxPlacement } = this.props;
            const className = `${prefixCls}-selection-column`;
            let lock = ColumnLock.left;
            if (rowBoxPlacement === RowBoxPlacement.start) {
                lock = ColumnLock.left;
            }
            else if (rowBoxPlacement === RowBoxPlacement.end) {
                lock = ColumnLock.right;
            }
            else {
                lock = false;
            }
            const selectionColumn = {
                key: SELECTION_KEY,
                resizable: false,
                titleEditable: false,
                headerClassName: className,
                className,
                footerClassName: className,
                renderer: this.renderSelectionBox,
                align: ColumnAlign.center,
                width: 50,
                lock,
            };
            if (dataSet && dataSet.selection === DataSetSelection.multiple) {
                selectionColumn.header = this.multipleSelectionRenderer;
                selectionColumn.footer = this.multipleSelectionRenderer;
            }
            return selectionColumn;
        }
        return undefined;
    }
    get draggableColumn() {
        const { dragColumnAlign, rowDraggable, prefixCls } = this;
        if (dragColumnAlign && rowDraggable) {
            const draggableColumn = {
                key: DRAG_KEY,
                resizable: false,
                titleEditable: false,
                className: `${prefixCls}-drag-column`,
                renderer: this.renderDragBox,
                align: ColumnAlign.center,
                width: 50,
            };
            if (dragColumnAlign === DragColumnAlign.left) {
                draggableColumn.lock = ColumnLock.left;
            }
            if (dragColumnAlign === DragColumnAlign.right) {
                draggableColumn.lock = ColumnLock.right;
                draggableColumn.header = this.customizable && this.customizedColumnHeader;
            }
            return draggableColumn;
        }
        return undefined;
    }
    get leftColumns() {
        const { dragColumnAlign, leftOriginalColumns, expandColumn, draggableColumn, rowNumberColumn, selectionColumn } = this;
        return observable.array([
            expandColumn,
            dragColumnAlign === DragColumnAlign.left ? draggableColumn : undefined,
            rowNumberColumn,
            selectionColumn && selectionColumn.lock === ColumnLock.left ? selectionColumn : undefined,
            ...leftOriginalColumns,
        ].filter(columnFilter));
    }
    get rightColumns() {
        const { dragColumnAlign, rightOriginalColumns, draggableColumn, customizedColumn, selectionColumn } = this;
        return observable.array([
            ...rightOriginalColumns,
            dragColumnAlign === DragColumnAlign.right ? draggableColumn : undefined,
            selectionColumn && selectionColumn.lock === ColumnLock.right ? selectionColumn : undefined,
            customizedColumn,
        ].filter(columnFilter));
    }
    get columns() {
        const { leftColumns, originalColumns, rightColumns } = this;
        return observable.array([
            ...leftColumns,
            ...originalColumns,
            ...rightColumns,
        ]);
    }
    get columnGroups() {
        return new ColumnGroups(this.columns, this.aggregation);
    }
    get leftColumnGroups() {
        return new ColumnGroups(this.leftColumns, this.aggregation);
    }
    get rightColumnGroups() {
        return new ColumnGroups(this.rightColumns, this.aggregation);
    }
    get leafNamedColumns() {
        return this.columnGroups.allLeafs.reduce((list, { column }) => column.name ? list.concat(column) : list, []);
    }
    get hasEmptyWidthColumn() {
        return this.columnGroups.leafs.some(({ column }) => isNil(get(column, 'width')));
    }
    get hasCheckFieldColumn() {
        const { checkField } = this.dataSet.props;
        if (checkField) {
            const { aggregation } = this;
            return this.columnGroups.leafs.some(({ column }) => aggregation
                ? treeSome([column], (c) => hasCheckField(c, checkField))
                : hasCheckField(column, checkField));
        }
        return false;
    }
    get hasFooter() {
        return this.columnGroups.leafs.some(({ column }) => !!column.footer && column.key !== SELECTION_KEY);
    }
    get isAnyColumnsLeftLock() {
        return this.leftColumns.length > 0;
    }
    get isAnyColumnsRightLock() {
        return this.rightColumns.length > 0;
    }
    get isAnyColumnsLock() {
        return this.isAnyColumnsLeftLock || this.isAnyColumnsRightLock;
    }
    get data() {
        const { filter, pristine } = this.props;
        const { dataSet, isTree, showCachedSelection } = this;
        let data = isTree ? dataSet.treeRecords : dataSet.records;
        if (typeof filter === 'function') {
            data = data.filter(filter);
        }
        if (pristine) {
            data = data.filter(record => !record.isNew);
        }
        if (showCachedSelection) {
            return [...dataSet.cachedSelected, ...data];
        }
        return data;
    }
    get indeterminate() {
        const { dataSet } = this;
        if (dataSet) {
            const selectedLength = dataSet.currentSelected.length;
            return !!selectedLength && selectedLength !== dataSet.records.length;
        }
        return false;
    }
    get allChecked() {
        const { dataSet } = this;
        if (dataSet) {
            const selectedLength = dataSet.currentSelected.length;
            return !!selectedLength && selectedLength === dataSet.records.length;
        }
        return false;
    }
    get expandIconAsCell() {
        const { expandedRowRenderer, expandIconAsCell } = this.props;
        if (expandIconAsCell !== undefined) {
            return expandIconAsCell;
        }
        return !!expandedRowRenderer && !this.isTree;
    }
    get expandIconColumnIndex() {
        if (this.expandIconAsCell) {
            return 0;
        }
        const { dragColumnAlign, rowDraggable, props: { expandIconColumnIndex = 0, rowNumber }, } = this;
        return expandIconColumnIndex + [this.hasRowBox, rowNumber, dragColumnAlign && rowDraggable].filter(Boolean).length;
    }
    get inlineEdit() {
        return this.props.editMode === TableEditMode.inline;
    }
    handleLoadCustomized() {
        this.initColumns();
        const { onAggregationChange, aggregation } = this.props;
        const { aggregation: customAggregation } = this.customized;
        if (onAggregationChange && customAggregation !== undefined && customAggregation !== aggregation) {
            onAggregationChange(customAggregation);
        }
    }
    isBuiltInColumn({ key }) {
        if (isString(key)) {
            return [DRAG_KEY, SELECTION_KEY, ROW_NUMBER_KEY, CUSTOMIZED_KEY, EXPAND_KEY].includes(key);
        }
    }
    getColumnTooltip(column) {
        const { tooltip } = column;
        if (tooltip) {
            return tooltip;
        }
        return getTooltip('table-cell');
    }
    getColumnHeaders() {
        const { leafNamedColumns, dataSet } = this;
        return getHeaderTexts(dataSet, leafNamedColumns.slice(), this.aggregation);
    }
    showEditor(name) {
        this.currentEditorName = name;
    }
    setLastScrollTop(lastScrollTop) {
        this.lastScrollTop = lastScrollTop;
    }
    hideEditor() {
        this.currentEditorName = undefined;
    }
    changeMouseBatchChooseIdList(idList) {
        this.mouseBatchChooseIdList = idList;
    }
    showNextEditor(name, reserve) {
        if (reserve) {
            this.dataSet.pre();
        }
        else {
            this.dataSet.next();
        }
        this.showEditor(name);
    }
    setProps(props) {
        this.props = props;
        const { showCachedSelection } = props;
        if (showCachedSelection !== undefined) {
            this.showCachedSelection = showCachedSelection;
        }
    }
    updateProps(props) {
        const { customizedCode } = this.props;
        this.setProps(props);
        if (this.customizable) {
            if (customizedCode !== props.customizedCode) {
                this.loadCustomized().then(this.handleLoadCustomized);
                return;
            }
            const { aggregation } = props;
            const { customized } = this;
            if (!isNil(aggregation) && aggregation !== customized.aggregation) {
                customized.aggregation = aggregation;
                this.saveCustomized();
            }
        }
        this.initColumns();
    }
    initColumns() {
        const { customized, customizable, aggregation, selectionColumn } = this;
        const { columns, children, rowBoxPlacement } = this.props;
        const customizedColumns = customizable ? customized.columns : undefined;
        const [leftOriginalColumns, originalColumns, rightOriginalColumns, hasAggregationColumn] = columns
            ? mergeDefaultProps(columns, aggregation, customizedColumns)
            : normalizeColumns(children, aggregation, customizedColumns);
        if (isNumber(rowBoxPlacement) && this.hasRowBox) {
            originalColumns.splice(rowBoxPlacement, 0, selectionColumn);
        }
        this.leftOriginalColumns = leftOriginalColumns;
        this.originalColumns = originalColumns;
        this.rightOriginalColumns = rightOriginalColumns;
        this.hasAggregationColumn = hasAggregationColumn;
    }
    isAggregationCellExpanded(record, key) {
        const expandedKeys = record.getState(AGGREGATION_EXPAND_CELL_KEY);
        if (expandedKeys) {
            return expandedKeys.includes(key);
        }
        return false;
    }
    setAggregationCellExpanded(record, key, expanded) {
        const expandedKeys = record.getState(AGGREGATION_EXPAND_CELL_KEY) || [];
        const index = expandedKeys.indexOf(key);
        if (expanded) {
            if (index === -1) {
                expandedKeys.push(key);
            }
        }
        else if (index !== -1) {
            expandedKeys.splice(index, 1);
        }
        record.setState(AGGREGATION_EXPAND_CELL_KEY, expandedKeys);
    }
    isRowExpanded(record) {
        const { isExpanded = this.expandedRows.indexOf(record.key) !== -1 } = record;
        return isExpanded && (!this.isTree || !record.parent || this.isRowExpanded(record.parent));
    }
    /**
     *
     * @param record 想修改的record
     * @param expanded 设置是否展开
     * @param disHandler 设置是否需要触发展开事件
     */
    setRowExpanded(record, expanded, disHandler) {
        record.isExpanded = expanded;
        if (!this.inBatchExpansion) {
            const index = this.expandedRows.indexOf(record.key);
            if (expanded) {
                if (index === -1) {
                    this.expandedRows.push(record.key);
                }
            }
            else if (index !== -1) {
                this.expandedRows.splice(index, 1);
            }
        }
        const { onExpand } = this.props;
        if (onExpand && !disHandler) {
            onExpand(expanded, record);
        }
        if (expanded && this.canTreeLoadData) {
            this.onTreeNodeLoad({ record });
        }
    }
    isRowPending(record) {
        return record.pending === true;
    }
    setRowPending(record, pending) {
        record.pending = pending;
    }
    isRowLoaded(record) {
        return record.childrenLoaded === true;
    }
    setRowLoaded(record, loaded) {
        record.childrenLoaded = loaded;
    }
    isRowHover(record) {
        return this.hoverRow === record;
    }
    get canTreeLoadData() {
        const { treeLoadData, treeAsync } = this.props;
        return treeAsync || !!treeLoadData;
    }
    get cellHighlightRenderer() {
        const { cellHighlightRenderer = getConfig('highlightRenderer') } = this.props;
        return cellHighlightRenderer;
    }
    setRowHover(record, hover) {
        this.hoverRow = hover ? record : undefined;
    }
    expandAll() {
        this.inBatchExpansion = true;
        this.expandedRows = this.dataSet.records.map(record => {
            this.setRowExpanded(record, true);
            return record.key;
        });
        this.inBatchExpansion = false;
    }
    collapseAll() {
        this.inBatchExpansion = true;
        this.dataSet.records.forEach(record => this.setRowExpanded(record, false));
        this.expandedRows = [];
        this.inBatchExpansion = false;
    }
    async onTreeNodeLoad({ record }) {
        const { dataSet, treeLoadData, treeAsync } = this.props;
        const promises = [];
        this.setRowPending(record, true);
        if (treeAsync && dataSet) {
            promises.push(dataSet.queryMoreChild(record));
        }
        if (treeLoadData) {
            promises.push(treeLoadData({ record, dataSet }));
        }
        try {
            await Promise.all(promises);
            this.setRowLoaded(record, true);
        }
        finally {
            this.setRowPending(record, false);
        }
    }
    renderSelectionBox({ record }) {
        return renderSelectionBox({ record, store: this });
    }
    renderRowNumber({ record, dataSet }) {
        const { isTree, props: { rowNumber } } = this;
        const numbers = getRowNumbers(record, dataSet, isTree);
        const number = numbers.join('-');
        if (typeof rowNumber === 'function') {
            return rowNumber({ record, dataSet, text: number, pathNumbers: numbers });
        }
        return number;
    }
    renderDragBox({ record }) {
        const { rowDragRender } = this.props;
        if (rowDragRender && isFunction(rowDragRender.renderIcon)) {
            return rowDragRender.renderIcon({ record });
        }
        return React.createElement(Icon, { type: "baseline-drag_indicator" });
    }
    changeCustomizedColumnValue(column, value) {
        const { customized: { columns } } = this;
        set(column, value);
        const columnKey = getColumnKey(column).toString();
        const oldCustomized = get(columns, columnKey);
        set(columns, columnKey, {
            ...oldCustomized,
            ...value,
        });
        this.saveCustomizedDebounce();
    }
    saveCustomized(customized) {
        if (this.customizable && this.customizedLoaded) {
            const { customizedCode } = this.props;
            if (customized) {
                this.customized = customized;
            }
            if (customizedCode) {
                const tableCustomizedSave = getConfig('tableCustomizedSave') || getConfig('customizedSave');
                tableCustomizedSave(customizedCode, this.customized, 'Table');
            }
        }
    }
    ;
    openCustomizationModal(modal) {
        const { node: { element }, height } = this;
        if (height === undefined) {
            this.totalHeight = element.offsetHeight;
        }
        const { customizedCode } = this.props;
        const modalProps = {
            drawer: true,
            size: Size.small,
            title: $l('Table', 'customization_settings'),
            children: React.createElement(CustomizationSettings, null),
            bodyStyle: {
                overflow: 'hidden auto',
                padding: 0,
            },
        };
        if (customizedCode) {
            modalProps.okText = $l('Table', 'save_button');
        }
        modal.open(modalProps);
    }
    async loadCustomized() {
        const { customizedCode } = this.props;
        if (this.customizable && customizedCode) {
            const tableCustomizedLoad = getConfig('tableCustomizedLoad') || getConfig('customizedLoad');
            runInAction(() => {
                delete this.customizedLoaded;
                this.loading = true;
            });
            try {
                const customized = await tableCustomizedLoad(customizedCode, 'Table');
                runInAction(() => {
                    this.customized = { columns: {}, ...customized };
                });
                this.customizedLoaded = true;
            }
            finally {
                runInAction(() => {
                    this.loading = false;
                });
            }
        }
    }
    handleAllPageSelectionMenuClick({ key }) {
        const { dataSet } = this;
        const { isAllPageSelection } = dataSet;
        switch (key) {
            case 'current': {
                if (this.allChecked) {
                    dataSet.unSelectAll();
                }
                else {
                    const { filter } = this.props;
                    dataSet.selectAll(filter);
                }
                break;
            }
            case 'all':
                dataSet.setAllPageSelection(!isAllPageSelection);
                break;
            default:
        }
    }
    renderAllPageSelectionMenu() {
        const { dataSet: { isAllPageSelection }, allChecked, prefixCls } = this;
        return (React.createElement(Menu, { prefixCls: `${prefixCls}-dropdown-menu`, onClick: this.handleAllPageSelectionMenuClick },
            React.createElement(Menu.Item, { key: "current" }, $l('Table', allChecked ? 'unselect_current_page' : 'select_current_page')),
            React.createElement(Menu.Item, { key: "all" }, $l('Table', isAllPageSelection ? 'unselect_all_page' : 'select_all_page'))));
    }
    multipleSelectionRenderer() {
        const buttons = [
            React.createElement(ObserverCheckBox, { key: "selectAll", checked: this.allChecked, indeterminate: this.indeterminate, onChange: this.handleSelectAllChange, value: true }),
        ];
        if (this.props.showAllPageSelectionButton) {
            buttons.push(React.createElement(Dropdown, { key: "selectAllPage", overlay: this.renderAllPageSelectionMenu },
                React.createElement(Icon, { type: "baseline-arrow_drop_down" })));
        }
        return buttons;
    }
}
__decorate([
    observable
], TableStore.prototype, "props", void 0);
__decorate([
    observable
], TableStore.prototype, "customized", void 0);
__decorate([
    observable
], TableStore.prototype, "tempCustomized", void 0);
__decorate([
    observable
], TableStore.prototype, "loading", void 0);
__decorate([
    observable
], TableStore.prototype, "leftOriginalColumns", void 0);
__decorate([
    observable
], TableStore.prototype, "originalColumns", void 0);
__decorate([
    observable
], TableStore.prototype, "rightOriginalColumns", void 0);
__decorate([
    observable
], TableStore.prototype, "hasAggregationColumn", void 0);
__decorate([
    observable
], TableStore.prototype, "calcBodyHeight", void 0);
__decorate([
    observable
], TableStore.prototype, "width", void 0);
__decorate([
    observable
], TableStore.prototype, "height", void 0);
__decorate([
    observable
], TableStore.prototype, "totalHeight", void 0);
__decorate([
    observable
], TableStore.prototype, "lastScrollTop", void 0);
__decorate([
    observable
], TableStore.prototype, "lockColumnsBodyRowsHeight", void 0);
__decorate([
    observable
], TableStore.prototype, "lockColumnsFootRowsHeight", void 0);
__decorate([
    observable
], TableStore.prototype, "lockColumnsHeadRowsHeight", void 0);
__decorate([
    observable
], TableStore.prototype, "expandedRows", void 0);
__decorate([
    observable
], TableStore.prototype, "hoverRow", void 0);
__decorate([
    observable
], TableStore.prototype, "rowClicked", void 0);
__decorate([
    observable
], TableStore.prototype, "currentEditorName", void 0);
__decorate([
    observable
], TableStore.prototype, "styledHidden", void 0);
__decorate([
    observable
], TableStore.prototype, "customizedActiveKey", void 0);
__decorate([
    observable
], TableStore.prototype, "mouseBatchChooseState", void 0);
__decorate([
    observable
], TableStore.prototype, "mouseBatchChooseIdList", void 0);
__decorate([
    observable
], TableStore.prototype, "columnResizing", void 0);
__decorate([
    observable
], TableStore.prototype, "scrollPosition", void 0);
__decorate([
    computed
], TableStore.prototype, "autoHeight", null);
__decorate([
    computed
], TableStore.prototype, "virtualStartIndex", null);
__decorate([
    computed
], TableStore.prototype, "virtualEndIndex", null);
__decorate([
    computed
], TableStore.prototype, "virtualData", null);
__decorate([
    computed
], TableStore.prototype, "currentEditRecord", null);
__decorate([
    observable
], TableStore.prototype, "showCachedSelection", void 0);
__decorate([
    computed
], TableStore.prototype, "overflowX", null);
__decorate([
    computed
], TableStore.prototype, "overflowY", null);
__decorate([
    autobind
], TableStore.prototype, "customizedColumnHeader", null);
__decorate([
    computed
], TableStore.prototype, "customizedColumn", null);
__decorate([
    computed
], TableStore.prototype, "expandColumn", null);
__decorate([
    computed
], TableStore.prototype, "rowNumberColumn", null);
__decorate([
    computed
], TableStore.prototype, "selectionColumn", null);
__decorate([
    computed
], TableStore.prototype, "draggableColumn", null);
__decorate([
    computed
], TableStore.prototype, "leftColumns", null);
__decorate([
    computed
], TableStore.prototype, "rightColumns", null);
__decorate([
    computed
], TableStore.prototype, "columns", null);
__decorate([
    computed
], TableStore.prototype, "columnGroups", null);
__decorate([
    computed
], TableStore.prototype, "leftColumnGroups", null);
__decorate([
    computed
], TableStore.prototype, "rightColumnGroups", null);
__decorate([
    computed
], TableStore.prototype, "leafNamedColumns", null);
__decorate([
    computed
], TableStore.prototype, "hasEmptyWidthColumn", null);
__decorate([
    computed
], TableStore.prototype, "hasCheckFieldColumn", null);
__decorate([
    computed
], TableStore.prototype, "data", null);
__decorate([
    computed
], TableStore.prototype, "indeterminate", null);
__decorate([
    computed
], TableStore.prototype, "allChecked", null);
__decorate([
    autobind,
    action
], TableStore.prototype, "handleLoadCustomized", null);
__decorate([
    action
], TableStore.prototype, "showEditor", null);
__decorate([
    action
], TableStore.prototype, "setLastScrollTop", null);
__decorate([
    action
], TableStore.prototype, "hideEditor", null);
__decorate([
    action
], TableStore.prototype, "changeMouseBatchChooseIdList", null);
__decorate([
    action
], TableStore.prototype, "setProps", null);
__decorate([
    action
], TableStore.prototype, "updateProps", null);
__decorate([
    action
], TableStore.prototype, "initColumns", null);
__decorate([
    action
], TableStore.prototype, "setAggregationCellExpanded", null);
__decorate([
    action
], TableStore.prototype, "setRowExpanded", null);
__decorate([
    action
], TableStore.prototype, "setRowPending", null);
__decorate([
    action
], TableStore.prototype, "setRowLoaded", null);
__decorate([
    computed
], TableStore.prototype, "cellHighlightRenderer", null);
__decorate([
    action
], TableStore.prototype, "setRowHover", null);
__decorate([
    action
], TableStore.prototype, "expandAll", null);
__decorate([
    action
], TableStore.prototype, "collapseAll", null);
__decorate([
    autobind
], TableStore.prototype, "onTreeNodeLoad", null);
__decorate([
    autobind
], TableStore.prototype, "renderSelectionBox", null);
__decorate([
    autobind
], TableStore.prototype, "renderRowNumber", null);
__decorate([
    autobind
], TableStore.prototype, "renderDragBox", null);
__decorate([
    action
], TableStore.prototype, "changeCustomizedColumnValue", null);
__decorate([
    action
], TableStore.prototype, "saveCustomized", null);
__decorate([
    autobind,
    action
], TableStore.prototype, "openCustomizationModal", null);
__decorate([
    autobind,
    action
], TableStore.prototype, "handleAllPageSelectionMenuClick", null);
__decorate([
    autobind
], TableStore.prototype, "renderAllPageSelectionMenu", null);
__decorate([
    autobind
], TableStore.prototype, "multipleSelectionRenderer", null);
